import { useEffect, useMemo, useRef, useState } from "react";
import { Download, FileText, RotateCcw, Sparkles } from "lucide-react";
import { CustomizationPanel } from "@/components/CustomizationPanel";
import { DownloadButton } from "@/components/DownloadButton";
import { FAQSection } from "@/components/FAQSection";
import { ManualPostEditor } from "@/components/ManualPostEditor";
import { PreviewCanvas } from "@/components/PreviewCanvas";
import { UrlInputPanel } from "@/components/UrlInputPanel";
import {
  defaultPost,
  defaultSettings,
  samplePost,
  storageKey,
} from "@/lib/constants";
import { exportPreviewImage } from "@/lib/exportImage";
import type { BeautifierSettings, PostContent, ToastState } from "@/lib/types";

type StoredState = {
  post?: Partial<PostContent>;
  settings?: Partial<BeautifierSettings>;
};

const fallbackImportMessage =
  "We couldn't automatically import this LinkedIn post. Paste the post text manually below.";

export function PostBeautifierPage() {
  const previewRef = useRef<HTMLDivElement>(null);
  const skipNextPersistRef = useRef(false);
  const [post, setPost] = useState<PostContent>(defaultPost);
  const [settings, setSettings] =
    useState<BeautifierSettings>(defaultSettings);
  const [hasLoadedStorage, setHasLoadedStorage] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [importMessage, setImportMessage] = useState("");
  const [toast, setToast] = useState<ToastState>(null);

  const hasContent = post.text.trim().length > 0;

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(storageKey);

      if (stored) {
        const parsed = JSON.parse(stored) as StoredState;
        setPost({ ...defaultPost, ...parsed.post });
        setSettings({ ...defaultSettings, ...parsed.settings });
      }
    } catch {
      setToast({
        type: "error",
        message: "Saved settings could not be restored.",
      });
    } finally {
      setHasLoadedStorage(true);
    }
  }, []);

  useEffect(() => {
    if (!hasLoadedStorage) {
      return;
    }

    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      window.localStorage.removeItem(storageKey);
      return;
    }

    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({
          post,
          settings,
        }),
      );
    } catch {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({
          post: {
            ...post,
            profileImage: keepSmallDataUrl(post.profileImage),
            profileHeaderImage: keepSmallDataUrl(post.profileHeaderImage),
            postImage: keepSmallDataUrl(post.postImage),
          },
          settings,
        }),
      );
    }
  }, [hasLoadedStorage, post, settings]);

  useEffect(() => {
    if (!toast) {
      return;
    }

    const timer = window.setTimeout(() => setToast(null), 4200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const updatePost = (updates: Partial<PostContent>) => {
    setPost((current) => ({ ...current, ...updates }));
  };

  const updateSettings = (updates: Partial<BeautifierSettings>) => {
    setSettings((current) => ({ ...current, ...updates }));
  };

  const handleImport = async () => {
    const trimmedUrl = post.url.trim();
    setImportMessage("");

    if (!trimmedUrl) {
      setImportMessage("Enter a LinkedIn post URL to try importing.");
      return;
    }

    setIsImporting(true);

    try {
      const response = await fetch("/api/import-linkedin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: trimmedUrl }),
      });
      const payload = (await response.json()) as {
        ok?: boolean;
        message?: string;
        data?: Partial<PostContent> | null;
      };

      if (response.ok && payload.ok && payload.data) {
        setPost((current) => ({ ...current, ...payload.data }));
        setImportMessage("Post details imported.");
        setToast({ type: "success", message: "Post imported." });
        return;
      }

      const message = payload.message ?? fallbackImportMessage;
      setPost({ ...defaultPost, url: trimmedUrl });
      setImportMessage(message);
      setToast({ type: "info", message });
    } catch {
      setPost({ ...defaultPost, url: trimmedUrl });
      setImportMessage(fallbackImportMessage);
      setToast({ type: "info", message: fallbackImportMessage });
    } finally {
      setIsImporting(false);
    }
  };

  const handleDownload = async () => {
    if (!previewRef.current || !hasContent) {
      return;
    }

    setIsExporting(true);

    try {
      await exportPreviewImage({
        node: previewRef.current,
        format: settings.exportFormat,
        quality: settings.jpgQuality,
      });
      setToast({
        type: "success",
        message: "Image downloaded successfully.",
      });
    } catch {
      setToast({
        type: "error",
        message:
          "Export failed. Try uploading images instead of using remote image URLs.",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const resetToDefaults = () => {
    setPost(defaultPost);
    setSettings(defaultSettings);
    setImportMessage("");
    setToast({ type: "success", message: "Design reset." });
  };

  const clearSavedData = () => {
    skipNextPersistRef.current = true;
    window.localStorage.removeItem(storageKey);
    setPost(defaultPost);
    setSettings(defaultSettings);
    setImportMessage("");
    setToast({ type: "success", message: "Saved browser data cleared." });
  };

  const useSampleContent = () => {
    setPost(samplePost);
    setToast({ type: "success", message: "Sample post loaded." });
  };

  const featureItems = useMemo(
    () => [
      "Cross platform LinkedIn posts",
      "LinkedIn to Instagram export",
      "LinkedIn to story images",
      "PNG or JPG downloads",
    ],
    [],
  );

  return (
    <main>
      <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-white/85 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <a
            href="#top"
            className="flex items-center gap-2 text-lg font-semibold text-ink"
          >
            <img
              src="/favicon.svg"
              alt=""
              className="h-8 w-8 rounded-lg"
              aria-hidden="true"
            />
            BeautifyIN
          </a>
          <nav
            aria-label="Primary navigation"
            className="hidden items-center gap-6 text-sm font-medium text-slate-600 md:flex"
          >
            <a className="hover:text-ink" href="#free-tools">
              Free Tools
            </a>
            <a className="hover:text-ink" href="#features">
              Features
            </a>
            <a className="hover:text-ink" href="#faq">
              FAQ
            </a>
          </nav>
          <a
            href="#editor"
            className="inline-flex items-center gap-2 rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
          >
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            Create export
          </a>
        </div>
      </header>

      <section id="top" className="mx-auto max-w-7xl px-4 pb-10 pt-10 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
          <div className="pt-4">
            <p className="mb-4 inline-flex rounded-lg border border-ocean/20 bg-white px-3 py-1 text-sm font-semibold text-ocean shadow-sm">
              Cross platform LinkedIn posts
            </p>
            <h1 className="max-w-3xl text-4xl font-bold leading-tight tracking-normal text-ink sm:text-5xl lg:text-6xl">
              BeautifyIN
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-600">
              Turn LinkedIn posts into beautiful images for Instagram, stories,
              slides, and cross-platform social sharing.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="#editor"
                className="inline-flex items-center justify-center rounded-lg bg-ocean px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-[#0f5d81]"
              >
                Cross platform LinkedIn posts
              </a>
              <a
                href="#editor"
                className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-ink shadow-sm transition hover:bg-slate-50"
              >
                LinkedIn to Instagram export
              </a>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {featureItems.map((item) => (
                <span
                  key={item}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>

          <section
            aria-labelledby="input-heading"
            className="rounded-lg border border-slate-200 bg-white p-4 shadow-panel sm:p-6"
          >
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <h2 id="input-heading" className="text-lg font-semibold text-ink">
                  Add your post
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  Import a public URL or paste the post content directly.
                </p>
              </div>
              <button
                type="button"
                onClick={useSampleContent}
                className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-ink transition hover:bg-slate-100"
              >
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                Sample
              </button>
            </div>
            <div className="space-y-5">
              <UrlInputPanel
                url={post.url}
                isImporting={isImporting}
                importMessage={importMessage}
                onUrlChange={(url) => updatePost({ url })}
                onImport={handleImport}
              />
              <ManualPostEditor post={post} onChange={updatePost} />
            </div>
          </section>
        </div>
      </section>

      <section
        id="editor"
        className="border-y border-slate-200/70 bg-white/60 py-10"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-ocean">
                Export studio
              </p>
              <h2 className="mt-2 text-3xl font-bold text-ink">
                Customize LinkedIn to Instagram exports
              </h2>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={resetToDefaults}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-ink shadow-sm transition hover:bg-slate-50"
              >
                <RotateCcw className="h-4 w-4" aria-hidden="true" />
                Reset
              </button>
              <button
                type="button"
                onClick={clearSavedData}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-ink shadow-sm transition hover:bg-slate-50"
              >
                <FileText className="h-4 w-4" aria-hidden="true" />
                Clear saved data
              </button>
              <DownloadButton
                disabled={!hasContent}
                isExporting={isExporting}
                onDownload={handleDownload}
              />
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-[380px_minmax(0,1fr)]">
            <CustomizationPanel
              settings={settings}
              onChange={updateSettings}
            />

            <section
              aria-label="Live preview"
              className="rounded-lg border border-slate-200 bg-white p-3 shadow-panel sm:p-5"
            >
              <div className="mb-4 flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
                <div>
                  <h3 className="text-lg font-semibold text-ink">Live preview</h3>
                  <p className="text-sm text-slate-600">
                    The export captures this design canvas only.
                  </p>
                </div>
                <span
                  className={`inline-flex w-fit items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold ${
                    hasContent
                      ? "bg-mint/10 text-emerald-800"
                      : "bg-slate-100 text-slate-700"
                  }`}
                >
                  {hasContent ? (
                    <Download className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <FileText className="h-4 w-4" aria-hidden="true" />
                  )}
                  {hasContent ? "Ready for export" : "Add post text"}
                </span>
              </div>
              <PreviewCanvas
                ref={previewRef}
                post={post}
                settings={settings}
              />
            </section>
          </div>
        </div>
      </section>

      <InfoSections />
      <FAQSection />

      <div
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 left-1/2 z-50 w-[min(calc(100%-2rem),440px)] -translate-x-1/2"
      >
        {toast ? (
          <div
            className={`rounded-lg border px-4 py-3 text-sm font-medium shadow-panel ${
              toast.type === "success"
                ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                : toast.type === "error"
                  ? "border-rose-200 bg-rose-50 text-rose-900"
                  : "border-sky-200 bg-sky-50 text-sky-900"
            }`}
          >
            {toast.message}
          </div>
        ) : null}
      </div>
    </main>
  );
}

function InfoSections() {
  const sections = [
    {
      id: "free-tools",
      title: "How it works",
      items: [
        "Paste a LinkedIn URL or enter post text",
        "Choose Instagram, LinkedIn, story, or slide size",
        "Download a cross-platform image",
      ],
    },
    {
      id: "features",
      title: "LinkedIn export options",
      items: ["Backgrounds", "Post image fitting", "Instagram and story sizes"],
    },
    {
      id: "use-cases",
      title: "Use cases",
      items: [
        "Cross-platform LinkedIn sharing",
        "LinkedIn to Instagram export",
        "Presentations",
        "Content portfolios",
        "Internal documentation",
        "Archiving posts",
      ],
    },
  ];

  return (
    <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
      <div className="grid gap-5 md:grid-cols-3">
        {sections.map((section) => (
          <section
            key={section.id}
            id={section.id}
            className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
          >
            <h2 className="text-xl font-bold text-ink">{section.title}</h2>
            <ul className="mt-5 space-y-3 text-sm leading-6 text-slate-600">
              {section.items.map((item) => (
                <li key={item} className="flex gap-3">
                  <span
                    className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-amber"
                    aria-hidden="true"
                  />
                  {item}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </section>
  );
}

function keepSmallDataUrl(value: string) {
  if (value.startsWith("data:") && value.length > 750_000) {
    return "";
  }

  return value;
}
